import { FC, Fragment, useState } from "react";
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from "hooks/useToggle";
// Components
import { IconButton } from 'components/Buttons';
import { Tooltip } from "components/Tooltip";
import PromoVideoEditDialog from "./PromoVideoEdit.dialog";
// Icons
import { FaEdit, FaVideo } from "react-icons/fa";
import { TbVideoPlus } from "react-icons/tb";

// Add buttons (page/video) default to a visible state instead of only
// showing up on hover — at low opacity they were easy to miss entirely.
// Hovering still bumps them up a bit further.
export const getAddButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: hovered ? 34 : 28,
  height: hovered ? 34 : 28,
  borderRadius: '50%',
  border: `2px solid ${hovered ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)'}`,
  background: hovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
  color: hovered ? '#fff' : 'rgba(255,255,255,0.9)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s ease',
  padding: 0,
  outline: 'none',
  opacity: disabled ? 0.3 : 1,
});

type Props = {
  promoVideo?: string;
  isGenerating: boolean;
  loading?: boolean;
}

// Leading tile shown before the first screen in QuizAgentChatScreen.
// promoVideo never renders in a QuizFrame preview (it's not part of
// SHARED_VISUAL_FIELDS / a page), so — unlike other screens — this shows a
// static mockup instead of an iframe, styled after the player's "page
// disabled" state rather than the `isGenerating` dashed-border placeholder,
// since the video itself is never actually playable here.
// Owns its own edit dialog (unlike per-screen pages, which share one dialog
// across many entries via QuizAgentChatScreen's selectedPage state) since
// there's exactly one promoVideo slot — no reason to push that state up.
const PromoVideoTile:FC<Props> = ({ promoVideo, isGenerating, loading = false }) => {
  const { t } = useTranslation();

  const { open: editOpen, toggle: toggleEdit } = useToggle();
  const [isAddHovered, setIsAddHovered] = useState(false);

  const dialog = editOpen ? (
    <PromoVideoEditDialog open={editOpen} onClose={toggleEdit} />
  ) : null;

  if (!promoVideo) {
    return (
      <Fragment>
        {dialog}
        <div
          className="flex-shrink-0 flex items-start justify-center"
          style={{ width: 32, height: '80vh', paddingTop: 48 }}
          onMouseEnter={() => setIsAddHovered(true)}
          onMouseLeave={() => setIsAddHovered(false)}
        >
          <Tooltip title={t('dialogs.quizAgent.addPromoVideoButton')}>
            <button
              onClick={toggleEdit}
              disabled={isGenerating || loading}
              style={getAddButtonStyle(isAddHovered, isGenerating || loading)}
            >
              <TbVideoPlus size={isAddHovered ? 16 : 14} />
            </button>
          </Tooltip>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {dialog}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-center gap-2 h-8">
          <p className="text-sm font-medium text-center capitalize" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {t('pages.quizForm.promoVideoLabel')}
          </p>
          {!isGenerating && (
            <Tooltip title={t('buttons.edit')}>
              <IconButton onClick={toggleEdit} size="sm" disabled={loading}>
                <FaEdit />
              </IconButton>
            </Tooltip>
          )}
        </div>
        <div className="relative my-4 mx-auto flex-shrink-0" style={{ width: '380px', height: '80vh' }}>
          <div
            className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center gap-4 p-4 rounded-xl"
            style={{ background: 'radial-gradient(circle at 50% 42%, #212c3a 0%, #12161d 75%)' }}
          >
            <div
              className="flex items-center justify-center rounded-full"
              style={{ width: 72, height: 72, border: '1.5px solid rgba(255,255,255,0.25)' }}
            >
              <FaVideo size={26} color="rgba(255,255,255,0.5)" />
            </div>
            <p className="text-center uppercase tracking-widest text-xs font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('dialogs.quizAgent.promoVideoPlaceholder')}
            </p>
          </div>
        </div>
      </div>
      {/* Spacer matching the between-screen gap width — without it the video
          tile sits flush against the next screen since it has no gap of its
          own (that logic only runs inside screens.map). */}
      <div className="flex-shrink-0" style={{ width: 32, height: '80vh' }} />
    </Fragment>
  );
}

export default PromoVideoTile;
